import { useParams } from 'react-router-dom';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import {
    MESSAGES,
    ORDER_BY_NUMBER_QUERY_PARAM,
    ORDER_BY_STRING_QUERY_PARAM,
    PAGE_SIZE,
} from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useDeferredValue, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import { RuntimeContext } from 'src/runtimeContext';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer';
import { MainLayout } from 'src/modules/common/layouts/MainLayout';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer';
import {
    getSensorsPageConfig,
    SensorProduct,
    SensorsPageData,
    SensorsPageVariables,
} from 'src/sensors/list/SensorsPageMapping.ts';
import { PATHS } from 'src/sensors/router.tsx';
import { Breadcrumbs } from 'src/sensors/Breadcrumbs.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { Query } from 'src/modules/common/components/Query/Query';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon.tsx';
import { prepareSensorAttributes } from 'src/sensors/list/prepareSensorAttributes.ts';
import { DirectionEnum, RegionsEnum, SensorsFilterPagePrimaryQueryVariables } from 'src/generated/operations.ts';
import { useOrderBy } from 'src/modules/common/hooks/useOrderBy.ts';
import { generateOrderItems } from 'src/utils/generateOrderItems.ts';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { useImperial } from 'src/sensors/hooks/useImperial';
import { getFilterValuesAsString } from 'src/common/Breadcrumb/getFilterValuesAsString';
import clsx from 'clsx';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { FilterSidebarComponent } from 'src/common/FilterSidebarComponent/FilterSidebarComponent.tsx';
import { DeviceGrid } from 'src/common/DeviceGrid/DeviceGrid.tsx';
import { Device } from 'src/common/Device/Device.tsx';
import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';
import { FilterEventDetails } from 'src/modules/common/types/FilterEventDetails.ts';
import { useLocationState } from 'src/sensors/useLocationState.ts';

const limit = PAGE_SIZE;

const stringItems: Array<keyof SensorProduct> = [
    'DisplayType',
    'SensorApplication',
    'OutputSignal',
    'MeasuredValues',
    'MeasuringRangeCO2',
    'MeasuringRangeRH',
    'AdditionalFeatures',
    'pressureAir',
    'certification',
    'mountingType',
];
const numberItems: Array<keyof SensorProduct> = [
    'ProbeLength',
    'CableLength',
    'm_flow',
];

export const SensorsPage = () => {
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const [locationState, setLocationState] = useLocationState();
    const pageConfig = useMemo(() => getSensorsPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const [
        searchParams,
        filter,
        checkboxHandler,,
        filtersReset,
    ] = useFilterLogic(pageConfig.filterConfig);

    const hasAdditionalFilter = useMemo(
        () => {
            if (pageConfig.filterConfig === null) {
                return false;
            }
            return getFilterValuesAsString(pageConfig.filterConfig, filter).length > 0;
        },
        [pageConfig.filterConfig, filter],
    );

    const { page } = useParams();

    const currentPage = useMemo(() => {
        const parsedPage = parseInt(page || '1', 10);
        return parsedPage < 1 ? 1 : parsedPage;
    }, [page]);

    const [orderByString, setOrderByString] = useOrderBy(ORDER_BY_STRING_QUERY_PARAM);
    const [orderByNumber, setOrderByNumber] = useOrderBy(ORDER_BY_NUMBER_QUERY_PARAM);

    useEffect(() => {
        if (currentPage.toString() !== locationState.sensorsPage) {
            setLocationState({
                ...locationState,
                sensorsPage: currentPage.toString(),
            });
        }
    }, [currentPage, locationState, setLocationState]);

    const filterComposed = composeFilter(
        [
            ...mandatoryFilterConfig.config,
            ...pageConfig.filterConfig,
        ],
        {
            ...mandatoryFilterConfig.values,
            ...filter,
        },
        userSettings.currentLocale,
        ['MeasuredValues'],
    );

    const variables = useMemo((): { variables: SensorsPageVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }

        const offset = (currentPage - 1) * limit;

        return {
            variables: {
                filter: filterComposed,
                locale: userSettings.currentLocale,
                offset,
                limit,
                orderByNumber,
                orderByString,
            },
        };
    }, [filter, currentPage, filterComposed, userSettings.currentLocale, orderByNumber, orderByString]);

    const filtersVariables = useMemo((): { variables: SensorsFilterPagePrimaryQueryVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }

        return {
            variables: {
                region: userSettings.currentRegion,
                filter: filterComposed,
                locale: userSettings.currentLocale,
            },
        };
    }, [filter, userSettings, filterComposed]);

    const defVariables = useDeferredValue(variables);

    const updatedLocation = useCallback((newPage: number) => {
        setLocationState({
            ...locationState,
            sensorsPage: newPage.toFixed(),
        });
    }, [setLocationState, locationState]);

    const onPageChange = usePageChange(
        (newPage: number) => `${PATHS.Sensors}/${newPage}`,
        updatedLocation,
    );

    const sensorsCheckboxHandler: CheckboxHandler = useCallback((detail: FilterEventDetails) => {
        onPageChange(1, searchParams);
        return checkboxHandler(detail);
    }, [onPageChange, checkboxHandler, searchParams]);

    const resetSensorFilters = useMemo(() => () => {
        onPageChange(1, searchParams);
        filtersReset(pageConfig.filterConfig);
    }, [onPageChange, searchParams, filtersReset, pageConfig.filterConfig]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="forward"
                disabled={locationState.sensorLabel.length < 2}
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Accessories,
                        encodeURIComponent(locationState.sensorLabel),
                        currentPage,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const breadcrumbProps: Breadcrumbs = [
        {
            selected: true,
            title: MESSAGES.SelectedSensor,
            disabled: true,
            value: locationState.sensorLabel,
            to: '#',
            actionShown: locationState.sensorLabel.length > 2,
            onActionClick: () => {
                setLocationState({
                    ...locationState,
                    sensorLabel: '-',
                    selectedAccessories: [],
                });
            },
        },
        {
            title: MESSAGES.SelectedAccessories,
            disabled: true,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            to: '#',
        },
    ];

    const { isImperial, showSwitch } = useImperial();

    const onUnitChange = useCallback((currentUnit: string) => {
        setLocationState({
            ...locationState,
            isImperial: currentUnit === MESSAGES.imperialUnit,
        });
    }, [locationState, setLocationState]);

    const onFlowRangeSelected = useCallback((selectedFlowRange: string) => {
        filtersReset([[locationState.selectedFlowRange, '']], {
            ...locationState,
            selectedFlowRange,
        });
    }, [filtersReset, locationState]);

    const isStale = defVariables !== variables;

    return (
        <div className="vss-sensors">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.SensorsPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <BreadcrumbContainer
                            isMobile={isMobile}
                            breadcrumbs={breadcrumbProps}
                            containerWrapperClass="vss-sensors-breadcrumbs"
                        />
                    )}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<SensorsPageData, SensorsPageVariables>
                        query={pageConfig.query}
                        variables={defVariables}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.list.results.length === 0) {
                                return (
                                    <div className={clsx({
                                        'vss-sensors-page': true,
                                        'vss-sensors-page--mobile': isMobile,
                                    })}
                                    >
                                        <FilterSidebarComponent
                                            label={MESSAGES.SensorsModalFilterLabel}
                                            filterConfigPrimary={pageConfig.filterConfigPrimary}
                                            filterConfigSecondary={pageConfig.filterConfigSecondary}
                                            queryPrimary={pageConfig.queryPrimary}
                                            genericFilterQuery={pageConfig.genericFilterQuery}
                                            variables={filtersVariables}
                                            checkboxHandler={sensorsCheckboxHandler}
                                            handleReset={resetSensorFilters}
                                            filter={filter}
                                            selectedFlowRange={locationState.selectedFlowRange}
                                            onFlowRangeSelected={onFlowRangeSelected}
                                        />

                                        <NoData/>
                                    </div>
                                );
                            }

                            const [firstProduct] = data.list.results;

                            const orderItemsString = generateOrderItems(
                                stringItems.filter((name) => !!firstProduct[name]) as string[],
                                pageConfig?.orderItemsConfig,
                                DirectionEnum.Asc,
                            );
                            const orderItemsNumber = generateOrderItems(
                                numberItems.filter((name) => !!firstProduct[name]) as string[],
                                pageConfig?.orderItemsConfig,
                                DirectionEnum.Asc,
                            );

                            return (
                                <div className={clsx({
                                    'vss-sensors-page': true,
                                    'vss-sensors-page--mobile': isMobile,
                                })}
                                >
                                    <FilterSidebarComponent
                                        label={MESSAGES.SensorsModalFilterLabel}
                                        filterConfigPrimary={pageConfig.filterConfigPrimary}
                                        filterConfigSecondary={pageConfig.filterConfigSecondary}
                                        queryPrimary={pageConfig.queryPrimary}
                                        genericFilterQuery={pageConfig.genericFilterQuery}
                                        variables={filtersVariables}
                                        checkboxHandler={sensorsCheckboxHandler}
                                        handleReset={resetSensorFilters}
                                        filter={filter}
                                        selectedFlowRange={locationState.selectedFlowRange}
                                        onFlowRangeSelected={onFlowRangeSelected}
                                    />

                                    <div className={clsx({
                                        'vss-sensors-page__list': true,
                                        'vss-sensors-page__list--stale': isStale,
                                    })}
                                    >
                                        <DeviceGrid
                                            isMobile={isMobile}
                                            hasAdditionalFilter={hasAdditionalFilter}
                                            currentPage={currentPage}
                                            pageSize={limit}
                                            total={data.list.count || 0}
                                            onPageChange={(p: number) => onPageChange(p, searchParams)}
                                            defaultOrderLabel={MESSAGES.Label}
                                            orderItems={[
                                                ...orderItemsNumber,
                                                ...orderItemsString,
                                            ]}
                                            orderSelected={[
                                                ...orderByNumber,
                                                ...orderByString,
                                            ]}
                                            onOrderByChange={(newOrder) => {
                                                if (newOrder.length > 0 &&
                                            orderItemsNumber.find(([, id]) => id === newOrder[0].id) !== undefined) {
                                                    onPageChange(
                                                        1,
                                                        setOrderByString(setOrderByNumber(searchParams, newOrder), []),
                                                    );
                                                } else {
                                                    onPageChange(
                                                        1,
                                                        setOrderByNumber(setOrderByString(searchParams, newOrder), []),
                                                    );
                                                }
                                            }}
                                            additionalActions={showSwitch && (
                                                <DropdownField
                                                    renderOption={(value) => <I18n messageId={value}/>}
                                                    selectedValue={isImperial
                                                        ? MESSAGES.imperialUnit
                                                        : MESSAGES.metricUnit}
                                                    options={[MESSAGES.metricUnit, MESSAGES.imperialUnit]}
                                                    onChange={onUnitChange}
                                                    size="md"
                                                    shaded={false}
                                                />
                                            )}
                                        >
                                            {data?.list.results.map((product) => (
                                                <Device
                                                    key={`${product.ID}_${product.Label}`}
                                                    defaultImage={<ValveIcon/>}
                                                    isSelected={locationState.sensorLabel === product.Label}
                                                    product={product}
                                                    attributes={prepareSensorAttributes(
                                                        product,
                                                        userSettings.currentRegion === RegionsEnum.Am,
                                                        isImperial,
                                                        locationState.selectedFlowRange,
                                                    )}
                                                    onDeviceClick={({ label }) => {
                                                        setLocationState({
                                                            ...locationState,
                                                            sensorLabel: label,
                                                            sensorsPage: currentPage.toString(),
                                                            selectedAccessories: [],
                                                            sent: false,
                                                            qty: 1,
                                                        });
                                                    }}
                                                />
                                            ))}
                                        </DeviceGrid>
                                    </div>
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
